import React from 'react';
import styled, { keyframes } from 'styled-components';
import { theme } from '../styles/theme';

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const fadeInUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(30px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const IndependenceSection = styled.section`
  padding: ${theme.spacing[20]} ${theme.spacing[4]};
  background: linear-gradient(135deg, ${theme.colors.white} 0%, ${theme.colors.gray[100]} 100%);
  position: relative;
  overflow: hidden;
  scroll-margin-top: ${theme.navHeight.desktop};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${theme.colors.primary}33, transparent);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 20%;
    right: -10%;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, ${theme.colors.primary}08 0%, transparent 70%);
    border-radius: 50%;
    animation: ${float} 6s ease-in-out infinite;
  }
  
  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing[12]} ${theme.spacing[4]};
    scroll-margin-top: ${theme.navHeight.mobile};
    
    &::after {
      width: 200px;
      height: 200px;
      top: 10%;
      right: -20%;
    }
  }
`;

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing[12]};
  animation: ${fadeInUp} 0.8s ease-out;
  
  @media (max-width: ${theme.breakpoints.md}) {
    margin-bottom: ${theme.spacing[8]};
  }
`;

const Title = styled.h2`
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.gray[900]};
  margin-bottom: ${theme.spacing[3]};
  position: relative;
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize['3xl']};
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.fontSize['2xl']};
  }
`;

const Tagline = styled.p`
  font-size: ${theme.typography.fontSize['xl']};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing[6]};
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize.lg};
  }
`;

const Description = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  line-height: 1.7;
  color: ${theme.colors.gray[700]};
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize.base};
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${theme.spacing[6]};
  margin-top: ${theme.spacing[12]};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[4]};
    margin-top: ${theme.spacing[8]};
  }
`;

const FeatureCard = styled.div`
  background: ${theme.colors.white};
  padding: ${theme.spacing[6]};
  border-radius: ${theme.borderRadius['2xl']};
  border: 1px solid ${theme.colors.gray[200]};
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  animation: ${fadeInUp} 0.8s ease-out;
  animation-delay: 0.2s;
  animation-fill-mode: both;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${theme.colors.primary}, ${theme.colors.secondary});
    transform: scaleX(0);
    transition: transform 0.3s ease;
    transform-origin: left;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    border-color: ${theme.colors.primary}33;
    
    &::before {
      transform: scaleX(1);
    }
  }
  
  &:nth-child(2) {
    animation-delay: 0.3s;
  }
  
  &:nth-child(3) {
    animation-delay: 0.4s;
  }
  
  &:nth-child(4) {
    animation-delay: 0.5s;
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing[4]};
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary});
  border-radius: ${theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.typography.fontSize['2xl']};
  margin-bottom: ${theme.spacing[4]};
  position: relative;
  transition: transform 0.3s ease;
  
  &::after {
    content: '';
    position: absolute;
    inset: -2px;
    background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary});
    border-radius: inherit;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  ${FeatureCard}:hover & {
    transform: scale(1.1);
    
    &::after {
      opacity: 0.2;
    }
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    width: 50px;
    height: 50px;
    font-size: ${theme.typography.fontSize.xl};
  }
`;

const FeatureContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const FeatureTitle = styled.h3`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.gray[900]};
  margin-bottom: ${theme.spacing[2]};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.fontSize.base};
  }
`;

const FeatureSubtitle = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing[1]};
`;

const FeatureDescription = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  line-height: 1.6;
  color: ${theme.colors.gray[600]};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.fontSize.xs};
  }
`;

const DecorativeElement = styled.div`
  position: absolute;
  bottom: 10%;
  left: -5%;
  width: 200px;
  height: 200px;
  background: linear-gradient(45deg, ${theme.colors.secondary}06, ${theme.colors.primary}06);
  border-radius: 50%;
  animation: ${float} 8s ease-in-out infinite reverse;
  
  @media (max-width: ${theme.breakpoints.md}) {
    width: 150px;
    height: 150px;
    left: -10%;
  }
`;

const Independence: React.FC = () => {
  return (
    <IndependenceSection id="independence">
      <DecorativeElement />
      <Container>
        <Header>
          <Title>Independence & Transparency</Title>
          <Tagline>Built for trust. Backed by data. Powered by community.</Tagline>
          <Description>
            <p>At Green Growers Club, we believe that cannabis should be honest, from seed to consumption.</p>
            <p>Our platform puts growers and consumers first by making transparency the standard, not the exception.</p>
            <p>No brand bias. No hidden agendas. Just verified facts and real feedback.</p>
          </Description>
        </Header>
        
        <FeaturesGrid>
          <FeatureCard>
            <FeatureIcon>🔍</FeatureIcon>
            <FeatureContent>
              <FeatureTitle>Unbiased Reviews</FeatureTitle>
              <FeatureSubtitle>Insights from real users.</FeatureSubtitle>
              <FeatureDescription>
                Every grower and strain is rated by the community, not brands or advertisers. Fair, transparent, and driven by the people.
              </FeatureDescription>
            </FeatureContent>
          </FeatureCard>
          
          <FeatureCard>
            <FeatureIcon>📊</FeatureIcon>
            <FeatureContent>
              <FeatureTitle>Transparent Data</FeatureTitle>
              <FeatureSubtitle>Let the numbers speak.</FeatureSubtitle>
              <FeatureDescription>
                From sustainability metrics to lab test results, we provide clear, honest data to help consumers make better choices.
              </FeatureDescription>
            </FeatureContent>
          </FeatureCard>
          
          <FeatureCard>
            <FeatureIcon>🤝</FeatureIcon>
            <FeatureContent>
              <FeatureTitle>Community First</FeatureTitle>
              <FeatureSubtitle>No brand influence.</FeatureSubtitle>
              <FeatureDescription>
                We don't sell placement or push promotions. Growers earn visibility through their work, not their wallets.
              </FeatureDescription>
            </FeatureContent>
          </FeatureCard>
          
          <FeatureCard>
            <FeatureIcon>🌱</FeatureIcon>
            <FeatureContent>
              <FeatureTitle>Sustainability First</FeatureTitle>
              <FeatureSubtitle>Support growers doing it right.</FeatureSubtitle>
              <FeatureDescription>
                Environmental impact is part of every grower profile. Track and display water, energy, and more.
              </FeatureDescription>
            </FeatureContent>
          </FeatureCard>
        </FeaturesGrid>
      </Container>
    </IndependenceSection>
  );
};

export default Independence; 